import { observer, useLocalObservable } from "mobx-react";
import { OFTableController } from "../OFTableController";
import { Button, Input, Tooltip } from "antd";
import { OFMultiSelect } from "../../OFMultiselect/OFMultiSelect";
import { translateKey as t } from "../../../locales/translation";
import { faAngleDown, faAngleLeft, faFilter, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";

const TableSearch = observer(({ ofTableController }: { ofTableController: OFTableController }) => {
  const state = useLocalObservable(() => ({
    search: "",
    expanded: false,
    inputRef: null,

    setSearch(state: string) {
      this.search = state;
    },
    setExpanded(state: boolean) {
      this.expanded = state;
    },
    setInputRef(state: any) {
      this.inputRef = state;
    },
  }));

  React.useEffect(() => {
    const { columnsAvailableForSearch, props } = ofTableController;

    ofTableController.setGlobalSearch(
      "selectedCols",
      columnsAvailableForSearch.map((col) => col._id)
    );

    if (props?.globalSearch && ofTableController.globalSearch.selectedCols.length === 0) {
      console.log(
        "%cTable search missing column configuration (add includeInSearch to the col)",
        "background: red; padding: 5px; color: #fff"
      );
    }
  }, [ofTableController]);

  const searchValidation = () => {
    const { columnsAvailableForSearch, globalSearch } = ofTableController;

    if (state.expanded && !state.search) {
      return false;
    }

    if (columnsAvailableForSearch.length === 0 || globalSearch.selectedCols.length === 0) {
      return false;
    }

    return true;
  };

  const resetTableSearch = () => {
    const { columnsAvailableForSearch } = ofTableController;

    ofTableController.setGlobalSearch("all", {
      query: "",
      selectedCols: columnsAvailableForSearch.map((col) => col._id),
    });

    state.setSearch("");
    state.setExpanded(false);
  };

  const renderDropDown = () => {
    const { globalSearch, columnsAvailableForSearch } = ofTableController;
    return (
      <OFMultiSelect
        data={columnsAvailableForSearch}
        renderItem={(item: any) => item.Header}
        selectedItems={globalSearch.selectedCols}
        menuWidth={200}
        onSelect={handleSelection}
        btnPlaceholder={
          <>
            <FontAwesomeIcon icon={faFilter} /> ({globalSearch.selectedCols.length})
            <FontAwesomeIcon icon={faAngleDown} style={{ marginLeft: 4 }} />
          </>
        }
        canSelectAll={true}
        size="small"
        style={{
          borderBottomRightRadius: 0,
          borderTopRightRadius: 0,
          marginRight: -1,
          borderColor: "#40a9ff",
          flex: 0,
        }}
      />
    );
  };

  const handleSelection = (val, state, selectedAll) => {
    const { columnsAvailableForSearch } = ofTableController;

    if (state) {
      if (selectedAll) {
        ofTableController.setGlobalSearch(
          "selectedCols",
          columnsAvailableForSearch.map((depot) => depot._id)
        );
      } else {
        let newSelectedCols = [...ofTableController.globalSearch.selectedCols];

        newSelectedCols.push(val._id);

        ofTableController.setGlobalSearch("selectedCols", newSelectedCols);
      }
    } else {
      if (selectedAll) {
        ofTableController.setGlobalSearch("selectedCols", []);
      } else {
        const itemIndex = ofTableController.globalSearch.selectedCols.findIndex((item) => item === val._id);

        let newSelectedCols = [...ofTableController.globalSearch.selectedCols];

        newSelectedCols.splice(itemIndex, 1);
        ofTableController.setGlobalSearch("selectedCols", newSelectedCols);
      }
    }
  };

  const renderSearchBtnTooltip = () => {
    if (!state.search) {
      return t("lblSearchInputCannotBeEmpty", "Search input cannot be empty");
    }

    if (ofTableController.globalSearch.selectedCols.length === 0) {
      return t("lblAtLeastOneColumnMustBeSelected", "At least 1 column must be selected");
    }

    return null;
  };

  const { props } = ofTableController;

  if (!props?.globalSearch) {
    return null;
  }

  return (
    <div className="flex">
      <div className="globalSearch" style={{ width: state.expanded ? 270 : 0 }}>
        {renderDropDown()}
        <Input
          size="small"
          placeholder={t("lblSearchTheTable", "Search the table")}
          onChange={(e) => {
            const value = e.target.value;

            if (!value) {
              ofTableController.setGlobalSearch("query", "");
            }

            state.setSearch(value);
          }}
          onPressEnter={() => ofTableController.setGlobalSearch("query", state.search)}
          value={state.search}
          className="searchInput"
          allowClear={true}
          ref={(ref) => (state.inputRef = ref)}
        />

        <Tooltip title={t("lblCollapse", "Collapse")}>
          <Button
            onClick={() => resetTableSearch()}
            size="small"
            icon={<FontAwesomeIcon icon={faAngleLeft} />}
            style={{
              borderRadius: 0,
              marginRight: -1,
              borderTop: "1px solid #40a9ff",
              borderBottom: "1px solid #40a9ff",
            }}
          />
        </Tooltip>

        <Tooltip title={renderSearchBtnTooltip()}>
          <Button
            size="small"
            icon={<FontAwesomeIcon icon={faSearch} />}
            style={{
              marginRight: "5px",
              borderBottomLeftRadius: 0,
              borderTopLeftRadius: 0,
              borderColor: "#40a9ff",
            }}
            onClick={() => ofTableController.setGlobalSearch("query", state.search)}
            disabled={!searchValidation}
            data-e2e="global-search-query-btn"
          />
        </Tooltip>
      </div>

      <Tooltip title={t("lblGlobalSearch", "Global Search")}>
        <Button
          onClick={() => {
            state.setExpanded(true);
            state.inputRef.focus();
          }}
          size="small"
          icon={<FontAwesomeIcon icon={faSearch} />}
          style={{ marginRight: "5px", display: state.expanded ? "none" : "block" }}
          data-e2e="global-search-open-btn"
        />
      </Tooltip>
    </div>
  );
});

export { TableSearch };
